import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { UntypedFormGroup, NgForm, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { DataFormService } from './data-form.service';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { AuditorUnavailabilityRequest, AuditorUnavailabilitySearch, DataFormChangeEvent, DataFormEvent, DateWindow } from './data-form.model';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { AlertService } from '../../services/alert.service';
import { FormlyJsonschema } from '@ngx-formly/core/json-schema';
import moment, { Moment } from 'moment';
import { AuditsService } from 'app/modules/audits/audits.service';
import { SearchOptionRequest } from 'app/shared/models/request/search-option-request.model';
import { QuoteService } from 'app/modules/quote/quote.service';
import { SalesQuoteRequestView } from 'app/modules/quote/quote.types';
import { DatePipe } from '@angular/common';
import { SalesQuoteLineItem } from 'app/shared/models/domain/data.model';
import * as StandardsConstants from 'app/constants/standard.constants';
import { SettingsService } from 'app/modules/admin/settings/settings.service';
import { AdminAuditorService } from 'app/modules/admin/personnel/admin-auditors/admin-auditors.service';
import { isString } from 'lodash';
import { environment } from 'environments/environment';
import { SalesQuoteView } from 'app/shared/models/domain/data-view.model';
import { getTestId, toUtcMidnightIso } from 'app/shared/functions/utils.type';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { HttpErrorResponse } from '@angular/common/http';
import { PageResponse } from '@iapmoit/library.angular.core';
import { AuditorUnavaibilityView } from 'app/modules/admin/personnel/admin-auditors/models/auditor-unavaibility-view.model';
import { DateRangeFilter, DateRangeProps } from './types/date-range/date-range.model';
@Component({
    selector: 'app-data-form',
    templateUrl: './data-form.component.html',
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, FormlyModule, MatButton, MatIcon],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataFormComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    @Input() formUrl: string;
    @Input() showConfirmationOnFormSubmit: boolean = false;
    @Input() formUrls: string[];
    @Input() formFields: FormlyFieldConfig[];
    @Input() apiUrl: string;
    @Input() model: any;
    @Input() dictionary: any;
    @Input() validators: any;
    @Input() idField: string;
    @Input() hideCancel: boolean = false;
    @Input() readOnly: boolean = false;
    @Input() hideSubmit: boolean = false;
    @Input() convertJson: boolean = false;
    @Input() skipApi: boolean = false;
    @Input() resetOnCancel: boolean = false;
    @Input() addExtension: boolean = true;
    @Input() title: string;
    @Input() description: string;
    @Input() client: any;
    @Input() buttonPositionAtEnd: boolean = false;
    @Input() role: string = 'admin';
    @Input() moduleName: string = '';
    @Output() submitForm: EventEmitter<DataFormEvent> = new EventEmitter<DataFormEvent>();
    @Output() cancelForm: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onChangeForm: EventEmitter<DataFormChangeEvent> = new EventEmitter<DataFormChangeEvent>();
    @ViewChild('dataForm') dataForm: NgForm;

    standardAuditors: any;
    dataModel: any;
    originalModel: any;
    form: UntypedFormGroup;
    options: FormlyFormOptions;
    fields: FormlyFieldConfig[];
    defaultFields: FormlyFieldConfig[];
    isDisabled: boolean = false;
    datePipe: DatePipe = new DatePipe('en-US');
    standardsConstants = StandardsConstants;
    getTestId = getTestId;

    private static readonly formContainerTestIds: Record<string, string> = {
        'audit-create-existing': 'modal-add-existing-audit',
    };

    private static readonly unavailabilityMonthsBefore: number = 1;
    private static readonly unavailabilityMonthsAfter: number = 24;
    private static readonly unavailabilityPageSize: number = 500;

    getFormContainerTestId(): string | null {
        return DataFormComponent.formContainerTestIds[this.formUrl] ?? null;
    }

    constructor(private readonly formService: DataFormService
        , private readonly changes: ChangeDetectorRef
        , private readonly formlyJsonschema: FormlyJsonschema
        , private readonly alertService: AlertService
        , private readonly auditService: AuditsService
        , private readonly quotesService: QuoteService
        , private readonly settingsService: SettingsService
        , private readonly auditorsService: AdminAuditorService
        , private readonly elementRef: ElementRef
    ) {
    }
    ngAfterViewInit(): void {
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {

    }

    ngOnChanges(changes: SimpleChanges): any {
        this.dataForm?.resetForm();
        if (changes.model && changes.model.currentValue) {
            this.dataModel = JSON.parse(JSON.stringify(changes.model.currentValue));
            this.originalModel = JSON.parse(JSON.stringify(changes.model.currentValue));
        }

        if (this.dataModel && this.dataModel['certificationTypeId']) {
            this.dataModel['certificationTypeId'] = this.dataModel['certificationTypeId'];
        }

        if (this.originalModel && this.originalModel['certificationTypeId']) {
            this.originalModel['certificationTypeId'] = this.originalModel['certificationTypeId'];
        }

        if (changes.formUrls && changes.formUrls.currentValue) {
            let fieldsArray: FormlyFieldConfig[] = [];
            const tasks: Observable<any>[] = [];
            for (let x: number = 0; x < changes.formUrls?.currentValue?.length; x++) {
                const addExtension = this.addExtension && !changes.formUrls?.currentValue[x]?.toString().toLowerCase().includes('api/');
                tasks.push(this.formService.loadForm(changes.formUrls?.currentValue[x], addExtension));
            }

            forkJoin(tasks).subscribe((fields) => {
                for (let x: number = 0; x < fields.length; x++) {
                    const processedFields = this.processFields(fields[x], changes, changes.formUrls?.currentValue[x]);
                    fieldsArray = [...fieldsArray, ...processedFields];
                }
                this.defaultFields = fieldsArray;
                this.initForm(fieldsArray);
            });

        } else if (changes.formUrl && changes.formUrl.currentValue) {
            this.formService.loadForm(changes.formUrl.currentValue, this.addExtension)
                .subscribe((fields) => {
                    const processedFields = this.processFields(fields, changes, changes.formUrl.currentValue);
                    this.defaultFields = processedFields;
                    this.initForm(processedFields);
                });
        }

        if (changes.formFields && changes.formFields.currentValue) {
            this.defaultFields = changes.formFields.currentValue;
            this.initForm(changes.formFields.currentValue);
        }

        if (changes.model && changes.model.currentValue) {
            if (this.defaultFields) {
                this.initForm(this.defaultFields);
            }
        }
    }

    processFields(fields, changes, formUrl): FormlyFieldConfig[] {

        if (true === this.convertJson) {
            fields = [this.formlyJsonschema.toFieldConfig(fields['schema'])];
        }

        if (formUrl === 'user-profile-password') {
            fields[0].validators = {
                validation: [
                    { name: 'fieldMatch', options: { errorPath: 'passwordConfirm' } },
                ]
            };
        }

        let fieldGroup = fields[0]?.fieldGroup[0]?.fieldGroup;
        let isOverrideControl = fieldGroup?.find(f => f.key === 'isOverride');

        if (this.model[this.idField]) {
            switch (formUrl) {
                case 'setting-audit-status':
                    const auditStatusControl = fieldGroup.find(f => f.key === 'auditStatus');
                    if (auditStatusControl?.key == 'auditStatus' && auditStatusControl.templateOptions) {
                        auditStatusControl.templateOptions.readonly = true;
                        auditStatusControl.templateOptions.disabled = true;
                    }
                    break;
                case 'setting-audit-type':
                    const auditTypeControl = fieldGroup.find(f => f.key === 'auditType');
                    if (auditTypeControl?.key == 'auditType' && auditTypeControl.templateOptions) {
                        auditTypeControl.templateOptions.readonly = true;
                        auditTypeControl.templateOptions.disabled = true;
                    }
                    break;
                case 'setting-standard':
                    const standardControl = fieldGroup.find(f => f.key === 'standardName');
                    if (standardControl?.key == 'standardName' && standardControl.templateOptions) {
                        standardControl.templateOptions.readonly = true;
                        standardControl.templateOptions.disabled = true;
                    }
                    break;
                case 'setting-quote-status':
                    const quoteStatusControl = fieldGroup.find(f => f.key === 'quoteStatus');
                    if (quoteStatusControl?.key == 'quoteStatus' && quoteStatusControl.templateOptions) {
                        quoteStatusControl.templateOptions.readonly = true;
                        quoteStatusControl.templateOptions.disabled = true;
                    }
                    break;
                case 'setting-sponsor-location':
                    const sponsoeLocationControl = fieldGroup.find(f => f.key === 'sponsorLocation');
                    if (sponsoeLocationControl?.key == 'sponsorLocation' && sponsoeLocationControl.templateOptions) {
                        sponsoeLocationControl.templateOptions.readonly = true;
                        sponsoeLocationControl.templateOptions.disabled = true;
                    }
                    break;
                case 'setting-certificate-status':
                    const certificateStatusControl = fieldGroup.find(f => f.key === 'certificateStatus');
                    if (certificateStatusControl?.key == 'certificateStatus' && certificateStatusControl.templateOptions) {
                        certificateStatusControl.templateOptions.readonly = true;
                        certificateStatusControl.templateOptions.disabled = true;
                    }
                    break;
            }
        } else {
            switch (formUrl) {
                case 'sales-quote-child-line-items-phase':
                    const salesQuoteProfileLineItemControl = fieldGroup.find(f => f.key === 'salesQuoteProfileLineItemId');
                    salesQuoteProfileLineItemControl.hooks = {
                        onInit: ((field) => {
                            field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                                if (!value) {
                                    return;
                                }
                                const temp = salesQuoteProfileLineItemControl?.templateOptions?.options?.find((option) => option.value === value)?.extra;

                                if (temp?.name) {
                                    const titleControl = fieldGroup.find(f => f.key === 'title');
                                    titleControl.formControl.setValue(temp.name);
                                }

                                if (!temp?.daysOnSite) {
                                    temp.daysOnSite = 0;
                                }

                                const daysOnsiteControl = fieldGroup.find(f => f.key === 'daysOnsite');
                                daysOnsiteControl.formControl.setValue(temp.daysOnSite);

                                if (!temp?.daysOffSite) {
                                    temp.daysOffSite = 0;
                                }

                                const daysOffsiteControl = fieldGroup.find(f => f.key === 'daysOffsite');
                                daysOffsiteControl.formControl.setValue(temp.daysOffSite);

                                if (temp?.auditTypeId) {
                                    const auditTypeControl = fieldGroup.find(f => f.key === 'auditTypeId');
                                    auditTypeControl.formControl.setValue(temp.auditTypeId);
                                }
                                if (temp?.fee && temp?.fee > 0) {
                                    const totalDays = temp.daysOffSite + temp.daysOnSite;
                                    const isOverrideControlTemp = fieldGroup.find(f => f.key === 'isOverride');
                                    isOverrideControlTemp.formControl.setValue(true);
                                    if (totalDays > 0) {
                                        const totalCostControl = fieldGroup.find(f => f.key === 'totalCost');
                                        totalCostControl.formControl.setValue(totalDays * temp.fee);
                                    }
                                }
                            });
                        })
                    }
                    break;
            }
        }

        if (formUrl === 'sales-quote-child-line-items-phase'
            || formUrl === 'invoice-item'
            || formUrl === 'sales-quote-child-line-items-phase-edit'
        ) {
            if (isOverrideControl) {
                let totalCostControl = fieldGroup.find(f => f.key === 'totalCost');

                function enableTotalCost(value) {
                    if (value == true) {
                        totalCostControl.formControl.enable({ emitEvent: false });
                        totalCostControl.formControl.setValidators([Validators.required]);
                    }
                    else {
                        totalCostControl.formControl.disable({ emitEvent: false });
                        totalCostControl.formControl.clearValidators();
                    }
                }

                isOverrideControl.hooks = {
                    onInit: ((field) => {
                        enableTotalCost(isOverrideControl.formControl.value);
                        field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                            enableTotalCost(value);
                        });
                    })
                }
            }
        }


        if (
            formUrl === 'audit-trip'
            || formUrl === 'auditor-unavailability'
            || formUrl === 'admin/auditor-unavailability'
        ) {
            const auditorIdControlForAuditTrip = fieldGroup.find(f => f.key === 'auditorId');
            const startDateControlForAuditTrip = fieldGroup.find(f => f.key === 'startDate');
            const endDateControlForAuditTrip = fieldGroup.find(f => f.key === 'endDate');
            if (auditorIdControlForAuditTrip) {
                auditorIdControlForAuditTrip.hooks = {
                    onInit: ((field) => {
                        field.formControl.valueChanges.pipe(
                            startWith(field.formControl.value),
                            filter((auditorId: number): boolean => !!auditorId),
                            distinctUntilChanged(),
                            switchMap((auditorId: number) => this.getAuditorUnavailableDays({ auditAuditorList: [auditorId] })),
                            takeUntil(this.destroy$)
                        ).subscribe((unavailableDays: Set<number>) => {
                            this.applyUnavailableDateFilter(unavailableDays, startDateControlForAuditTrip, endDateControlForAuditTrip);
                        });
                    })
                }
            }
        }

        if (formUrl === 'auditor-travel-time') {
            let travelTimeControl = fieldGroup.find(f => f.key === 'travelTime');
            let travelRateControl = fieldGroup.find(f => f.key === 'travelRate');
            let totalAmountControl = fieldGroup.find(f => f.key === 'totalAmount');
            travelTimeControl.hooks = {
                onInit: ((field) => {
                    field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                        if (!value) {
                            return;
                        }
                        totalAmountControl?.formControl?.setValue(value * parseFloat(travelRateControl?.formControl?.value));
                    });
                })
            };
        }

        if (formUrl === 'client-audit' || formUrl === 'all-year-quote-audit') {
            let quoteIdControl = fieldGroup?.find(f => f.key === 'salesQuoteId');
            let quoteRequestLocationId = fieldGroup?.find(f => f.key === 'quoteRequestLocationId');
            let salesQuoteLineItemIdControl = fieldGroup?.find(f => f.key === 'salesQuoteLineItemId');
            let standardIdControl = fieldGroup?.find(f => f.key === 'standardId');
            let auditStatusControl = fieldGroup?.find(f => f.key === 'auditStatusId');
            let auditDaysControl = fieldGroup?.find(f => f.key === 'auditDays');
            let auditDaysOnSiteControl = fieldGroup?.find(f => f.key === 'auditDays_OnSite');
            let auditDaysOffSiteControl = fieldGroup?.find(f => f.key === 'auditDays_OffSite');
            let auditTypeControl = fieldGroup?.find(f => f.key === 'auditTypeId');
            let leadAuditorIdControl = fieldGroup?.find(f => f.key === 'leadAuditorId');
            let auditorIdControl = fieldGroup?.find(f => f.key === 'auditorIds');

            auditorIdControl.hooks = {
                onInit: ((field) => {
                    setTimeout(() => {
                        const auditorList = [];
                        this.standardAuditors?.map(rows => {
                            if (auditorIdControl?.formControl?.value?.find(row => row == rows.value)) {
                                auditorList.push(rows);
                            }
                        });
                        auditorIdControl.formControl.updateValueAndValidity();
                        this.processLeadAuditorData(leadAuditorIdControl, auditorList, auditorIdControl);
                    }, 1500);

                    field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                        if (!value) {
                            return;
                        }
                        const auditorList = [];
                        this.standardAuditors?.map(rows => {
                            if (value.find(row => row == rows.value)) {
                                auditorList.push(rows);
                            }
                        });
                        setTimeout(() => {
                            this.processLeadAuditorData(leadAuditorIdControl, auditorList, auditorIdControl);
                        }, 100);
                    })
                })
            }

            if (formUrl === 'client-audit') {
                auditStatusControl.hooks = {
                    onInit: ((field) => {
                        field.formControl.setValue(field.formControl.value ?? 1);
                    })
                }

                const request = {
                    itemsPerPage: environment.defaultAllItemsPerPage,
                    currentPage: 1,
                    clientId: this.client?.clientId,
                    sort: 'createdDate',
                    sortDir: 'desc'
                };
                if (!this.dictionary?.salesQuoteList) {
                    this.quotesService.getAllSalesQuoteViews(request, this.role).subscribe((response) => {
                        const quoteList = response.items.map((quote) => {
                            return {
                                value: quote?.id?.toString(),
                                label: `${this.datePipe.transform(quote.createdDate)} - ${quote?.standardName?.toString()}`,
                                extra: quote
                            };
                        })

                        quoteIdControl.templateOptions.options = quoteList;
                        this.changes.markForCheck();
                    });
                }

                quoteIdControl.hooks = {
                    onInit: ((field) => {
                        field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                            if (!value) {
                                return;
                            }
                            const quote = quoteIdControl.templateOptions.options?.find((option) => option.value === value)?.extra as SalesQuoteView;
                            const event: DataFormChangeEvent = {
                                key: 'quoteId',
                                value: value,
                                model: null
                            };
                            standardIdControl.formControl.setValue(quote?.standardId);
                            auditDaysControl.formControl.setValue(null);
                            auditDaysOnSiteControl.formControl.setValue(null);
                            auditDaysOffSiteControl.formControl.setValue(null);
                            auditTypeControl.formControl.setValue(null);
                            this.quotesService.getSalesQuoteView(quote?.quoteRequestId).subscribe((view: SalesQuoteRequestView) => {
                                event.model = view;
                                const mainLocation = view?.mainSite;
                                quoteRequestLocationId.templateOptions.options = [];
                                quoteRequestLocationId.templateOptions.options = view.quoteRequestLocations?.map((location) => {
                                    return {
                                        value: location?.id?.toString(),
                                        label: `${location?.siteName} - ${location?.address1} - ${location?.address2}`,
                                        extra: location
                                    };
                                });
                                const tempSalesQuoteLineItemsOptions = this.buildQuoteLineItemOptions(view, mainLocation);
                                salesQuoteLineItemIdControl.templateOptions.options = tempSalesQuoteLineItemsOptions.filter(x => x.value !== null);
                                this.changes.markForCheck();
                                this.onChangeForm.emit(event);
                            });


                        });
                    })
                };

                salesQuoteLineItemIdControl.hooks = {
                    onInit: ((field) => {
                        field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                            if (!value) {
                                return;
                            }
                            const lineItem = salesQuoteLineItemIdControl.templateOptions.options?.find((option) => option.value === value)?.extra as SalesQuoteLineItem;
                            auditDaysControl.formControl.setValue((lineItem?.daysOnsite ?? 0) + (lineItem?.daysOffsite ?? 0));
                            auditDaysOnSiteControl.formControl.setValue(lineItem?.daysOnsite ?? 0);
                            auditDaysOffSiteControl.formControl.setValue(lineItem?.daysOffsite ?? 0);
                            auditTypeControl.formControl.setValue(lineItem?.auditTypeId);
                            this.changes.markForCheck();
                        })
                    })
                };
            }

            standardIdControl.hooks = {
                onInit: ((field) => {
                    field.formControl.valueChanges.pipe(
                        takeUntil(this.destroy$),
                        map((standardId: string) => {
                            this.setAuditorsForStandardId(standardId, fieldGroup);
                        }),
                    ).subscribe();

                    if (this.dataModel?.standardId) {
                        this.setAuditorsForStandardId(this.dataModel.standardId, fieldGroup);
                    }
                })
            }
        }

        if (formUrl === 'create-certificate' || formUrl === 'revise-certificate') {
            fields[0].fieldGroup[2].fieldGroup[0].hooks = {
                onInit: ((field) => {
                    field.formControl.valueChanges.pipe(
                        takeUntil(this.destroy$),
                        map((value: Moment) => {
                            if (value) {
                                let expirationDate = value.clone();
                                expirationDate.add(3, 'years');
                                expirationDate.subtract(1, 'day');
                                field.form.get('expireDate')?.setValue(expirationDate);
                            }
                        }),
                    ).subscribe();
                })
            };
        }

        if (formUrl === 'audit-ballot' || formUrl === 'audit-ballot-update') {
            const openDateField = fieldGroup.find(f => f.key === 'openDate');
            if (openDateField) {
                openDateField.hooks = {
                    onInit: ((field) => {
                        field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                            if (value) {
                                const closeDateControl = field.form.get('closeDate');
                                const decisionDateControl = field.form.get('decisionDate');
                                if (closeDateControl && !closeDateControl.value) {
                                    closeDateControl.setValue(value);
                                }
                                if (decisionDateControl && !decisionDateControl.value) {
                                    decisionDateControl.setValue(value);
                                }
                            }
                        });
                    })
                };
            }
        }

        if (formUrl === 'setting-expense-report-options') {
            const fieldGroupTemp = fields[0].fieldGroup[0].fieldGroup;
            const unitPriceByActionControl = fieldGroupTemp?.find(f => f.key === 'unitPriceByAction');
            const tempOptions = [];
            tempOptions.push({ label: 'Please select', value: null });
            unitPriceByActionControl.templateOptions.options?.map((rows) => {
                // if (this.dataModel?.usedServices?.findIndex(rl => rl == rows.value) == -1) {
                tempOptions.push(rows);
                // }
            });
            unitPriceByActionControl.templateOptions.options = tempOptions;
            this.changes.markForCheck();

        }
        if (formUrl === 'document-revision') {
            let fieldGroup = fields[0].fieldGroup[0].fieldGroup;
            let revisionTypeControl = fieldGroup?.find(f => f.key === 'documentRevisionTypeId');
            let fieldGroup2 = fields[0].fieldGroup[2]?.fieldGroup;
            let markupMediaIdControl = fieldGroup2?.find(f => f.key === 'markupMediaId');
            let finalMediaIdControl = fieldGroup2?.find(f => f.key === 'finalMediaId');
            let fieldGroup3 = fields[0].fieldGroup[3]?.fieldGroup;
            let infoControl = fieldGroup3?.find(f => f.key === 'info');

            if (changes.model.currentValue) {
                this.updateFieldForDocuments(changes.model.currentValue?.documentRevisionTypeId, markupMediaIdControl, finalMediaIdControl, infoControl);
            }

            if (revisionTypeControl) {
                revisionTypeControl.hooks = {
                    onInit: ((field) => {
                        field.formControl.valueChanges.pipe(
                            takeUntil(this.destroy$),
                            map((value: any) => {
                                this.updateFieldForDocuments(value, markupMediaIdControl, finalMediaIdControl, infoControl);
                            }),
                        ).subscribe();
                    })
                };
            }
        }

        if (
            formUrl === 'client-basic' || formUrl === 'client-basic-mmr' || formUrl === 'client-basic-add' || formUrl === 'client-basic-add-sales-rep'
            || formUrl === 'sales-rep-basic' || formUrl === 'mmr-basic' || formUrl === 'auditor-admin' || formUrl === 'client-additional-site'
        ) {
            const fieldGroup = fields[0].fieldGroup[0].fieldGroup;
            let stateProvince = fieldGroup.find(f => f.key === 'stateProvinceId');
            let country = fieldGroup.find(f => f.key === 'countryId');
            let industryCode = fieldGroup.find(f => f.key === 'industryCodeId');
            let industryCompetency = fieldGroup.find(f => f.key === 'industryCompetencyId');
            if (fields[0].fieldGroup?.length > 1 && !industryCompetency) {
                fields[0].fieldGroup?.map((rowsField) => {
                    if (!industryCompetency) {
                        const tempFieldGroup = rowsField.fieldGroup;
                        industryCode = tempFieldGroup.find(f => f.key === 'industryCodeId');
                        industryCompetency = tempFieldGroup.find(f => f.key === 'industryCompetencyId');
                    }
                })
            }
            if (fields[0].fieldGroup?.length > 1 && !stateProvince) {
                fields[0].fieldGroup?.map((rowsField) => {
                    if (!stateProvince) {
                        const tempFieldGroup = rowsField.fieldGroup;
                        stateProvince = tempFieldGroup.find(f => f.key === 'stateProvinceId');
                        country = tempFieldGroup.find(f => f.key === 'countryId');
                    }
                });
            }

            let isIndustryCompetency = false;
            if (industryCompetency) {
                if (!changes?.model?.currentValue?.clientId) {
                    industryCompetency.className = industryCompetency.className + ' hidden';
                } else if (this.dictionary?.clientStandardsList && !this.dictionary?.clientStandardsList?.find(rows => rows?.standardName?.includes('13485'))) {
                    industryCompetency.className = industryCompetency.className + ' hidden';
                } else {
                    if (industryCode) {
                        isIndustryCompetency = true;
                    }
                }

                if (this.dictionary?.clientStandardsList?.length > 0) {
                    this.settingsService.getIndustryCompetenciesOptionsList(this.dictionary?.clientStandardsList?.map(c => c.standardId))
                        .subscribe((response) => {
                            industryCompetency.templateOptions.options = response;
                            this.changes.markForCheck();
                        });
                }

                if (industryCode) {
                    industryCode.hooks = {
                        onInit: ((field) => {
                            industryCompetency.templateOptions.label = 'IAF Industry Competency';
                            industryCompetency.templateOptions.placeholder = 'IAF Industry Competency';
                            if (isIndustryCompetency) {
                                field.className = industryCompetency.className + ' hidden';
                                setTimeout(() => {
                                    field.formControl.setValue(null);
                                    field.formControl.setValidators([]);
                                    field.formControl.clearValidators();
                                    field.formControl.updateValueAndValidity();
                                    setTimeout(() => {
                                        const tempInput = document.getElementById('formly_21_input_clientNumber_0');
                                        if (tempInput) {
                                            tempInput.focus();
                                        }
                                        this.changes.markForCheck();
                                    }, 100);
                                }, 1000);

                            }
                        })
                    };
                }
            }

            if (country) {
                country.hooks = {
                    onInit: ((field) => {
                        const filteredStateList1 = this.dictionary.stateList.filter(c => c.extra == changes?.model?.currentValue?.countryId);
                        const selectedState = filteredStateList1.find(rows => rows.value == changes?.model?.currentValue?.stateProvinceId);
                        stateProvince.templateOptions.options = filteredStateList1;
                        stateProvince.templateOptions.required = filteredStateList1.length > 0 ? true : false;
                        stateProvince.templateOptions.readonly = filteredStateList1.length > 0 ? false : true;
                        stateProvince.templateOptions.disabled = filteredStateList1.length > 0 ? false : true;
                        if (!selectedState) {
                            stateProvince.formControl.setValue(null);
                        }
                        if (filteredStateList1?.length) {
                            stateProvince.formControl.setValidators(Validators.required);
                        } else {
                            stateProvince.formControl.clearValidators();
                        }
                        field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                            const filteredStateList = this.dictionary.stateList.filter(c => c.extra == value);
                            stateProvince.templateOptions.options = filteredStateList;
                            stateProvince.templateOptions.required = filteredStateList.length > 0 ? true : false;
                            stateProvince.templateOptions.readonly = filteredStateList.length > 0 ? false : true;
                            stateProvince.templateOptions.disabled = filteredStateList.length > 0 ? false : true;
                            if (filteredStateList?.length) {
                                stateProvince.formControl.setValue(null);
                                stateProvince.formControl.setValidators(Validators.required);
                            } else {
                                stateProvince.formControl.clearValidators();
                            }
                            this.changes.markForCheck();
                        });
                        stateProvince.formControl.updateValueAndValidity();
                    })
                };
            }
        }
        return fields;
    }

    processLeadAuditorData(leadAuditorIdControl, auditorList, auditorIdControl): void {
        leadAuditorIdControl.templateOptions.options = auditorList;
        if (auditorList?.length === 1) {
            leadAuditorIdControl.formControl.setValue(auditorList[0]?.value);
            leadAuditorIdControl.formControl.setValidators([Validators.required]);
        }
        else if (auditorList?.length > 1) {
            let currentLead = leadAuditorIdControl.value;
            if (currentLead && !auditorList.find(rows => rows.value == currentLead)) {
                leadAuditorIdControl.formControl.setValue(null);
            }
            leadAuditorIdControl.formControl.setValidators([Validators.required]);
        }
        else {
            leadAuditorIdControl.formControl.setValue(null);
            leadAuditorIdControl.formControl.setValidators([]);
        }
        leadAuditorIdControl.formControl.updateValueAndValidity();
        this.changes.markForCheck();
    }

    updateFieldForDocuments(value, markupMediaIdControl, finalMediaIdControl, infoControl): void {
        if (value && markupMediaIdControl && finalMediaIdControl) {
            markupMediaIdControl.className = 'w-1/2 mr-4';
            finalMediaIdControl.className = 'w-1/2';
            infoControl.className = '';
            markupMediaIdControl.templateOptions.required = true;
            finalMediaIdControl.templateOptions.required = true;

            if (value == 3) {
                markupMediaIdControl.className = 'hidden';
                finalMediaIdControl.className = 'hidden';
                infoControl.className = 'hidden';
                markupMediaIdControl.templateOptions.required = false;
                finalMediaIdControl.templateOptions.required = false;
            }
        }
    }

    onSubmit(): any {
        if (this.showConfirmationOnFormSubmit) {
            this.alertService.confirm('Save Changes and Notify SCB Staff?', 'Are you sure you want to save changes? This action will automatically notify SCB Staff about the changes.').then((result) => {
                if (result?.isConfirmed) {
                    this.onSubmitForm();
                }
            });
        } else {
            this.onSubmitForm();
        }
    }

    resetForm(): void {
        this.form.reset();
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.dataForm.reset();
        this.dataForm.resetForm();
        this.options.resetModel();
        this.model = {};
        this.isDisabled = false;

        if (this.resetOnCancel) {
            this.form = null;
        }
    }

    showErrorToast(error: HttpErrorResponse): void {
        const errorCodes = [400, 404, 500];
        if (errorCodes.includes(error?.status)) {
            const errorMessage = this.getErrorMessage(error);
            this.alertService.errorToast('Error!', errorMessage);
        }
        else {
            this.alertService.errorToast('Error!', 'Something went wrong.');
        }
        this.isDisabled = false;
    }

    getMediaRequests(): Array<Observable<any>> {
        const requests: Observable<any>[] = [];
        this.dataModel['mediaId'].forEach((mediaId) => {
            const model = { ...this.dataModel };
            model['mediaId'] = mediaId;
            requests.push(this.processApiUrl(model));
        });
        return requests;
    }

    emitFormService(event: DataFormEvent): void {
        this.submitForm.emit(event);
        this.formService.onFormEvent$.emit(event);
    }

    private applyPreSubmitFormSpecificAdjustments(): void {
        if (this.shouldResetStateProvince()) {
            const stateList = this.dictionary?.stateList?.filter(c => c.extra == this.dataModel?.countryId) ?? [];
            if (stateList.length == 0 && this.dataModel?.stateProvinceId) {
                this.dataModel.stateProvinceId = 0;
            }
        }

        if (this.formUrl == 'client-document') {
            const hasMediaChanged = this.dataModel['mediaId'] !== this.originalModel['mediaId'];
            if (hasMediaChanged && this.dataModel['documentStatusId'] == 4) {
                this.dataModel['documentStatusId'] = 2;
            }
        }
    }

    private shouldResetStateProvince(): boolean {
        return this.formUrl == 'client-basic'
            || this.formUrl == 'client-basic-mmr'
            || this.formUrl == 'client-basic-add'
            || this.formUrl == 'client-basic-add-sales-rep'
            || this.formUrl == 'sales-rep-basic'
            || this.formUrl == 'mmr-basic'
            || this.formUrl == 'auditor-admin'
            || this.formUrl == 'client-additional-site';
    }

    private buildSubmitRequest(parsedModel: any): Observable<any> {
        if (true == this.skipApi) {
            return of({ item: parsedModel });
        }

        this.prepareDataModelForApiSubmission();
        const request = this.getSubmissionRequest();
        this.normalizeSingleFileUploadFields();
        return request;
    }

    private prepareDataModelForApiSubmission(): void {
        if (this.dataModel?.distributionId && Array.isArray(this.dataModel?.distributionId)) {
            this.dataModel.distributionId?.forEach((distributionRow) => {
                this.dataModel[distributionRow] = true;
            });
        }

        if (this.dataModel?.startDate && this.dataModel?.startDate['endDateKey']) {
            // Convert to UTC midnight ISO string to avoid timezone issues
            const start = this.dataModel?.startDate['dates']?.startDate;
            const end = this.dataModel?.startDate['dates']?.endDate;
            this.dataModel[this.dataModel?.startDate['endDateKey']] = toUtcMidnightIso(end);
            this.dataModel['startDate'] = toUtcMidnightIso(start);
        }
    }

    private getSubmissionRequest(): Observable<any> {
        if (this.dataModel?.mediaId && Array.isArray(this.dataModel?.mediaId)) {
            const requests: Observable<any>[] = this.getMediaRequests();
            return forkJoin(requests);
        }

        return this.processApiUrl(this.dataModel);
    }

    private normalizeSingleFileUploadFields(): void {
        Object.keys(this.dataModel).forEach(key => {
            const value = this.dataModel[key];
            const field = this.scanForm(this.fields[0], key, false);
            if (key !== 'mediaId' && Array.isArray(value) && field?.type === 'uploadFile' && field?.templateOptions?.multiple !== true) {
                this.dataModel[key] = value?.length > 0 ? value[0] : null;
            }
        });
    }

    private isMsWordResponse(data: any): boolean {
        return data?.body && data?.body?.type == 'application/msword';
    }

    private downloadMsWordFile(data: any): void {
        const fileName = this.extractFileName(data);
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(data.body);
        a.href = objectUrl;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(objectUrl);
    }

    private extractFileName(data: any): string {
        let fileName = '';
        const contentDisposition = data.headers.get('Content-Disposition');
        if (contentDisposition) {
            const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = fileNameRegex.exec(contentDisposition);
            if (matches != null && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '');
            }
        }

        return fileName;
    }

    private emitSubmitEvent(data: any, parsedModel: any): void {
        let currentData = null;

        if (!this.isMsWordResponse(data)) {
            if (Array.isArray(data)) {
                currentData = data
                    .map(response => response?.item)
                    .filter(Boolean);
            } else {
                currentData = data?.item;
            }
        }
        const event: DataFormEvent = {
            formUrl: this.formUrl
            , formUrls: this.formUrls
            , originalData: this.originalModel
            , currentData
            , dirty: this.form.dirty
            , model: parsedModel
        };
        this.emitFormService(event);
    }

    onSubmitForm(): void {
        this.applyPreSubmitFormSpecificAdjustments();

        const parsedModel = this.dataModel ? JSON.parse(JSON.stringify(this.dataModel)) : [];
        if (this?.client) {
            this.dataModel.clientId = this?.client?.clientId;
        }
        const request = this.buildSubmitRequest(parsedModel);

        this.isDisabled = true;
        request.subscribe((data) => {
            if (this.isMsWordResponse(data)) {
                this.downloadMsWordFile(data);
            }

            this.emitSubmitEvent(data, parsedModel);
            this.resetForm();
        }, (error: HttpErrorResponse) => {
            this.showErrorToast(error);
        });
    }

    getErrorMessage(error): any {
        let errorMessage = 'Something went wrong.';
        try {
            const messageJson = error?.error?.exceptionMessage || error?.error?.ExceptionMessage;
            const messageObject = messageJson ? (this.isJsonString(messageJson) ? JSON.parse(messageJson) : messageJson) : {};
            if (!isString(messageObject)) {
                Object.keys(messageObject).every(key => {
                    const value = messageObject[key];
                    const errors = value?.Errors;
                    if (errors && Array.isArray(errors) && errors.length > 0) {
                        errorMessage = errors[0]?.ErrorMessage;
                        return false;
                    }
                    return true;
                });
            } else {
                errorMessage = messageObject;
            }
        }
        catch (e) {
            return errorMessage;
        }
        return errorMessage;
    }

    isJsonString(str): boolean {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    onCancel(): any {
        // this.alertService.confirm('Are you sure?', 'Are you sure you want to close?').then((result: any) => {
        //     if (result.isConfirmed) {
        this.model = null;
        this.originalModel = null;
        this.dataModel = null;
        this.form = new UntypedFormGroup({});
        this.form.reset();
        this.dataForm.reset();
        this.options.resetModel();
        if (
            this.resetOnCancel
        ) {
            this.form = null;
        }
        // this.form = null;
        // this.form.reset();
        // this.changes.markForCheck();
        // this.changes.detectChanges();
        this.cancelForm.emit(true);
        //     }
        // });
    }

    private getAuditorUnavailableDays(search: AuditorUnavailabilitySearch): Observable<Set<number>> {
        const dateWindow = this.getUnavailabilityWindow();
        const request: AuditorUnavailabilityRequest = {
            itemsPerPage: DataFormComponent.unavailabilityPageSize,
            currentPage: 1,
            sort: 'startDate',
            sortDir: 'asc',
            auditAuditorList: search?.auditAuditorList ?? [],
            startDate: dateWindow.startDate,
            endDate: dateWindow.endDate
        };

        return this.auditorsService.getAuditorUnavaibility(request).pipe(
            map((response: PageResponse<AuditorUnavaibilityView>) => this.getUnavailableDays(response?.items)),
            catchError(() => of(new Set<number>()))
        );
    }

    private applyUnavailableDateFilter(
        unavailableDays: Set<number>,
        startDateField: FormlyFieldConfig<DateRangeProps>,
        endDateField: FormlyFieldConfig<DateRangeProps>
    ): void {
        const dateFilter = this.createUnavailableDateFilter(unavailableDays);

        if (startDateField?.templateOptions) {
            startDateField.templateOptions.filter = dateFilter;
        }
        if (startDateField?.templateOptions?.datepickerOptions) {
            startDateField.templateOptions.datepickerOptions.filter = dateFilter;
        }
        if (endDateField?.templateOptions?.datepickerOptions) {
            endDateField.templateOptions.datepickerOptions.filter = dateFilter;
        }
        this.changes.markForCheck();
    }

    private getUnavailabilityWindow(): DateWindow {
        const today = moment().startOf('day');
        const editedStartDate = moment(this.originalModel?.startDate || null);
        const anchor = editedStartDate.isValid() && editedStartDate.isBefore(today) ? editedStartDate.startOf('day') : today;
        return {
            startDate: anchor.clone().subtract(DataFormComponent.unavailabilityMonthsBefore, 'months').toDate(),
            endDate: anchor.clone().add(DataFormComponent.unavailabilityMonthsAfter, 'months').toDate()
        };
    }

    private getUnavailableDays(items: AuditorUnavaibilityView[] | null | undefined): Set<number> {
        const days = new Set<number>();
        items?.forEach((row: AuditorUnavaibilityView) => {
            const day = moment(row.startDate).startOf('day');
            const lastDay = moment(row.endDate).startOf('day');
            while (day.isValid() && lastDay.isValid() && day.isSameOrBefore(lastDay)) {
                days.add(day.valueOf());
                day.add(1, 'day');
            }
        });
        return days;
    }

    private createUnavailableDateFilter(unavailableDays: Set<number>): DateRangeFilter {
        return (date: Date | Moment | null): boolean => {
            if (!date) {
                return true;
            }
            return !unavailableDays.has(moment(date).startOf('day').valueOf());
        };
    }

    /**
     * Removes null/undefined entries and any leaf field that has neither a `type`
     * nor a `fieldGroup`. Such empty fields render an empty mat-form-field whose
     * FormlyFieldControl is undefined, causing "Cannot read properties of undefined
     * (reading 'controlType')" at runtime. Runs recursively over nested fieldGroups.
     */
    private sanitizeFields(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
        if (!Array.isArray(fields)) {
            return [];
        }
        return fields
            .filter((field) => !!field)
            .map((field) => {
                if (Array.isArray(field.fieldGroup)) {
                    field.fieldGroup = this.sanitizeFields(field.fieldGroup);
                }
                return field;
            })
            .filter((field) => !!field.type || (Array.isArray(field.fieldGroup) && field.fieldGroup.length > 0) || !!field.template);
    }

    public initForm(fields: FormlyFieldConfig[]): any {
        this.form = new UntypedFormGroup({});
        this.options = {};
        this.fields = this.sanitizeFields(fields);

        if (this.fields[0] && this.fields[0].fieldGroup && this.fields[0].fieldGroup.length) {
            this.fields[0]?.fieldGroup?.map((rows) => {
                if (this.readOnly) {
                }
                return rows?.fieldGroup?.map((rowChild) => {
                    if (this.readOnly && rowChild?.type) {
                        rowChild.templateOptions = rowChild.templateOptions || {};
                        rowChild.templateOptions.readonly = true;
                        rowChild.templateOptions.disabled = true;
                    }
                    return rowChild;
                });
            });
        }

        if (this.fields[0] && this.fields[0].fieldGroup && this.fields[0].fieldGroup.length) {
            this.fields[0]?.fieldGroup[0]?.fieldGroup?.map((rows) => {
                if (this.readOnly) {
                    rows.templateOptions = rows.templateOptions || {};
                    rows.templateOptions.readonly = true;
                    rows.templateOptions.disabled = true;
                }
                return rows;
            });
        }


        if (this.dictionary) {
            if (true === this.convertJson) {
            }
            else {
                Object.keys(this.dictionary).forEach((key) => {
                    let tempKey = key.replace('List', 'Id');
                    if (tempKey === 'stateId') {
                        tempKey = 'stateProvinceId';
                    }
                    if (tempKey === 'clientStatusId') {
                        tempKey = 'salesStatusId';
                    }
                    if (tempKey === 'documentStatusId') {
                        tempKey = 'documentStatusId';
                    }
                    if (tempKey === 'recordTypeId') {
                        tempKey = 'recordType';
                    }
                    if (tempKey === 'auditorId') {
                        tempKey = 'auditorId';
                    }
                    if (tempKey === 'certificateNumberOptionId') {
                        tempKey = 'certificateNumber';
                    }
                    if (tempKey === 'attachmentTypeId') {
                        tempKey = 'attachmentType';
                    }
                    if (tempKey === 'technicalReviewerId') {
                        tempKey = 'reviewerId';
                    }
                    if (tempKey === 'documentTypeId' && this.formUrl !== 'document-basic') {
                        tempKey = 'documentType';
                    }
                    let target = this.scanForm(this.fields[0], tempKey, false);
                    if (!target) {
                        target = this.scanForm(this.fields[0], tempKey?.replace('id', 'Id'), false);
                    }
                    if (target) {
                        let tempOptions = this.dictionary[key]?.map((rows) => {
                            // Only parseInt for numeric fields, never for auditDocumentTypeId or documentType
                            if (
                                tempKey !== 'distributionId' &&
                                tempKey !== 'salesQuoteProfileLineItemId' &&
                                tempKey !== 'quoteRequestLocationId' &&
                                tempKey !== 'auditDocumentTypeId' &&
                                tempKey !== 'auditTypeId' &&
                                tempKey !== 'documentType' &&
                                tempKey !== 'salesQuoteId' &&
                                tempKey !== 'salesQuoteLineItemId' &&
                                tempKey !== 'industryCompetencyId'
                            ) {
                                // Only parse if value is a number and not NaN
                                if (rows.value !== undefined && rows.value !== null && rows.value !== '' && !Number.isNaN(rows.value) && typeof rows.value === 'string') {
                                    rows.value = parseInt(rows.value);
                                }
                            }
                            // For auditDocumentTypeId and documentType, always keep as string
                            return rows;
                        });
                        if ((tempKey == 'salesQuoteId' || tempKey == 'salesQuoteLineItemId') && tempOptions) {
                            tempOptions = this.dictionary[key]?.map((rows) => {
                                if (rows.extra !== undefined && rows.extra !== null && typeof rows.extra?.id === 'string') {
                                    rows.value = rows.extra.id;
                                }

                                return rows;
                            });
                        }
                        if ((tempKey == 'auditDocumentTypeId' || tempKey == 'documentType') && tempOptions) {
                            tempOptions.sort(function (a, b) {
                                if (a.label?.toLowerCase() < b.label?.toLowerCase()) {
                                    return -1;
                                }
                                if (a.label?.toLowerCase() > b.label?.toLowerCase()) {
                                    return 1;
                                }
                                return 0;
                            });
                        }
                        target.templateOptions.options = tempOptions;
                    }
                });
            }
        }

        if (this.validators) {

            if (true === this.convertJson) {
            }
            else {
                Object.keys(this.validators)
                    .forEach((key) => {
                        if (this.validators[key] && this.validators[key].length) {
                            const target: FormlyFieldConfig = this.scanForm(this.fields[0], key, false);
                            if (target) {
                                if (!target.asyncValidators) {
                                    target.asyncValidators = {};
                                }
                                for (let x: number = 0; x < this.validators[key].length; x++) {
                                    switch (this.validators[key][x]) {
                                        case 'usernameUnique':
                                            this.formService.setModel = this.model;
                                            target.asyncValidators[key] = {
                                                expression: this.formService.validateUniqueUsername.bind(this.formService),
                                                message: 'This username is already taken.',
                                            }
                                            break;

                                        case 'emailUnique':
                                            this.formService.setModel = this.model;
                                            if (target) {
                                                target.asyncValidators[key] = {
                                                    expression: this.formService.validateUniqueEmail.bind(this.formService),
                                                    message: 'This email is already taken.',
                                                }
                                                if (target.templateOptions) {
                                                    target.templateOptions.change = (field, $event) => {
                                                        const tempTarget: FormlyFieldConfig = this.scanForm(this.fields[0], 'username', false);
                                                        if (tempTarget) {
                                                            tempTarget.formControl.setValue(target.formControl.value);
                                                        }
                                                        this.changes.markForCheck();
                                                    };
                                                }
                                            }
                                            break;

                                        case 'contactEmailUnique':
                                            this.formService.setModel = this.model;
                                            target.asyncValidators[key] = {
                                                expression: this.formService.validateContactUniqueEmail.bind(this.formService),
                                                message: 'This email is already taken.',
                                            }
                                            target.templateOptions.change = (field, $event) => {
                                                const tempTarget: FormlyFieldConfig = this.scanForm(this.fields[0], key, false);
                                                tempTarget.formControl.setValue(target.formControl.value);
                                                this.changes.markForCheck();
                                            };
                                            break;

                                        case 'ignore':
                                            if (target.asyncValidators[key]) {
                                                delete target.asyncValidators[key];
                                            }
                                            break;
                                    }
                                }
                            }
                        }
                    });
            }
        }

        this.changes.markForCheck();
        this.applyFieldTestIds(fields);
    }

    private applyFieldTestIds(fields: FormlyFieldConfig[]): void {
        setTimeout(() => {
            const allFields = this.collectFields(fields);
            for (const field of allFields) {
                const templateOptions: any = field.templateOptions;
                const attributesTestId = templateOptions?.attributes?.['data-testid'];
                const directTestId = templateOptions?.['data-testid'];
                const testId = attributesTestId ?? directTestId;
                if (testId && field.id) {
                    const rawId = String(field.id);
                    const escapedId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
                        ? CSS.escape(rawId)
                        : rawId.replaceAll('"', String.raw`\"`);
                    const escapedFormlyId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
                        ? CSS.escape(`formly_${rawId}`)
                        : `formly_${rawId}`.replaceAll('"', String.raw`\"`);

                    const fieldEl = this.elementRef.nativeElement.querySelector(`formly-field[id="${escapedId}"]`)
                        ?? this.elementRef.nativeElement.querySelector(`[id*="${escapedFormlyId}"]`)?.closest('formly-field');
                    if (fieldEl) {
                        fieldEl.dataset['testid'] = testId;
                    }
                }
            }
        });
    }

    private collectFields(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
        const result: FormlyFieldConfig[] = [];
        for (const field of fields) {
            result.push(field);
            if (field.fieldGroup) {
                result.push(...this.collectFields(field.fieldGroup));
            }
        }
        return result;
    }

    private setAuditorsForStandardId(standardId: string, fieldGroup: any): void {
        let client = this.client;
        if (!client && this.model?.client) {
            client = this.model?.client;
        }
        if (standardId) {
            this.auditService.getAuditorsByStandardId(
                [standardId],
                client?.sponsorLocaionId,
                client?.industryCodeId
            ).subscribe((response) => {
                const items = response.items.filter((auditor) => {
                    return !auditor.hasCompetencies || !this?.client?.industryCompetencyId || auditor.competencies?.toLowerCase().includes(client?.industryCompetencyId?.toLowerCase());
                });
                const auditorList = items.map((auditor) => {
                    return {
                        value: auditor.auditorId.toString(),
                        label: auditor.auditorName
                    } as SearchOptionRequest;
                });
                this.standardAuditors = auditorList;
                fieldGroup.find(fieldGroup => fieldGroup.key === 'auditorIds').templateOptions.options = auditorList;
                this.changes.markForCheck();
            });
        }
    }

    private buildQuoteLineItemOptions(view: SalesQuoteRequestView, mainLocation: any): { value: string; label: string; extra: SalesQuoteLineItem }[] {
        const filteredItems = view?.childrenLineItems?.filter(c => c.auditTypeId > 0 && (!c.quoteRequestLocationId || c.quoteRequestLocationId === mainLocation?.id)) ?? [];
        const allItems = view?.childrenLineItems?.filter(c => c.auditTypeId > 0) ?? [];
        const groups = [...new Set(allItems.map(c => `${c.parentId}|${c.auditTypeId}`))];
        const fallbacks = groups
            .filter(key => !filteredItems.some(c => `${c.parentId}|${c.auditTypeId}` === key))
            .map(key => allItems.find(c => `${c.parentId}|${c.auditTypeId}` === key));
        return [...filteredItems, ...fallbacks]
            .sort((x, y) => Date.parse(x.created) - Date.parse(y.created))
            .map((item) => {
                const parent = view?.parentLineItems?.find(p => p?.id === item?.parentId);
                const otherSites = view?.childrenLineItems?.filter(c => item?.salesQuoteProfileLineItemId && c.salesQuoteProfileLineItemId === item?.salesQuoteProfileLineItemId);
                if (otherSites?.length > 0) {
                    item.daysOffsite = otherSites.reduce((a, b) => a + (b?.daysOffsite ?? 0), 0);
                    item.daysOnsite = otherSites.reduce((a, b) => a + (b?.daysOnsite ?? 0), 0);
                } else {
                    item.daysOffsite = parent?.daysOffsite ?? 0;
                    item.daysOnsite = parent?.daysOnsite ?? 0;
                }
                return {
                    value: item?.id?.toString(),
                    label: parent?.title ? `${parent?.title} - ${item?.title}` : `${item?.title}`,
                    extra: item
                };
            });
    }

    private scanForm(field: FormlyFieldConfig, searchKey: string, checkType: boolean): FormlyFieldConfig {

        if (field && field?.key && field?.key === searchKey) {
            return field;
        } else if (checkType && field && field?.type && field?.type === searchKey) {
            return field;
        } else {
            if (field && field?.fieldGroup && field?.fieldGroup?.length) {
                for (let x: number = 0; x < field?.fieldGroup?.length; x++) {
                    const childField = field?.fieldGroup[x];
                    const result = this.scanForm(childField, searchKey, checkType);
                    if (result !== null) {
                        return result;
                    }
                }
            }
            return null;
        }
    }

    private processApiUrl(model: any): Observable<any> {
        let request: Observable<any>;
        if (this.idField && this.model[this.idField]) {
            request = this.formService.putForm(this.apiUrl + '/' + this.model[this.idField], model);
        } else {
            request = this.formService.postForm(this.apiUrl, model);
        }
        return request;
    }
}
